// services/communication/chatSocket.ts
//
// socket.io-client wrapper for the /chat namespace. Requires
// `socket.io-client` in the FE package.json — parent installs when
// deploying.
//
// Events emitted by the BE gateway (see chat.gateway.ts):
//   message:new, message:edited, message:deleted
//   reaction:added, reaction:removed
//   typing:start, typing:stop
//   read:updated
//
// Consumers should:
//   1. call `connectChatSocket(token)` once on chat page mount
//   2. call `subscribeToConversation(conversationId)` when the user opens one
//   3. wire the `on(...)` events they care about
//   4. call `disconnectChatSocket()` on unmount

import type { Socket } from 'socket.io-client'
import { readEnv } from '@edemics/lib/env'

let socket: Socket | null = null
let connectPromise: Promise<Socket> | null = null

function base(): string {
  return (
    readEnv('API_BASE_URL') ||
    readEnv('API_URL') ||
    'http://localhost:3009'
  )
}

/**
 * Connect (idempotent). Returns the connected Socket instance.
 */
export async function connectChatSocket(token: string): Promise<Socket> {
  if (socket && socket.connected) return socket
  if (connectPromise) return connectPromise
  connectPromise = (async () => {
    // Dynamic import so the module doesn't fail to compile if socket.io-client
    // isn't installed yet — the runtime call is where the failure will surface.
    const { io } = await import('socket.io-client')
    const s = io(`${base()}/chat`, {
      query: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
    })
    await new Promise<void>((resolve, reject) => {
      s.once('connect', () => resolve())
      s.once('connect_error', (err) => reject(err))
    })
    socket = s
    return s
  })()
  try {
    return await connectPromise
  } finally {
    connectPromise = null
  }
}

export function disconnectChatSocket(): void {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

export function getChatSocket(): Socket | null {
  return socket
}

export function subscribeToConversation(conversationId: string): void {
  socket?.emit('subscribe-conversation', { conversationId })
}

export function unsubscribeFromConversation(conversationId: string): void {
  socket?.emit('unsubscribe-conversation', { conversationId })
}

// Convenience wrappers for on/off
type Handler<T = any> = (payload: T) => void

export function onChatEvent<T = any>(event: string, handler: Handler<T>): () => void {
  socket?.on(event, handler)
  return () => {
    socket?.off(event, handler)
  }
}
